import { useState } from 'react';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { Check, ArrowRight, Plus, Minus, Gift } from 'lucide-react';

const plans = [
  {
    name: 'Plano MEI',
    price: 'R$ 49,90',
    desc: 'Para Microempreendedor Individual',
    highlighted: false,
    badge: null,
    features: [
      'DAS mensal emitido e acompanhado',
      'Declaração anual (DASN-SIMEI)',
      'Orientação sobre emissão de notas fiscais',
      'Alerta quando chegar perto do limite de R$ 81 mil/ano',
      'Atendimento por WhatsApp em horário comercial',
    ],
    cta: 'Quero o plano MEI',
    link: 'https://api.whatsapp.com/send/?phone=5562994767293&text=Ol%C3%A1!%20Tenho%20interesse%20no%20Plano%20MEI%20(R$%2049,90).&type=phone_number&app_absent=0',
    style: 'outline',
  },
  {
    name: 'Plano Simples Serviços',
    price: 'R$ 147,00',
    desc: 'Para ME e EPP prestadores de serviço',
    highlighted: true,
    badge: 'MAIS CONTRATADO',
    features: [
      'Contabilidade completa (balancete, livros, ECD quando obrigatório)',
      'Apuração mensal do Simples Nacional (DAS)',
      'Todas as declarações acessórias (DEFIS, DCTFWeb, EFD-Reinf)',
      'Departamento pessoal para até 2 integrantes inclusos',
      'Pró-labore e folha mensal',
      '1 alteração contratual simples por ano inclusa',
      'Revisão trimestral do melhor anexo do Simples',
      'Atendimento WhatsApp + reunião trimestral por vídeo',
      'Funcionário CLT extra: +R$ 19,90/mês',
    ],
    cta: 'Quero o plano Serviços',
    link: 'https://api.whatsapp.com/send/?phone=5562994767293&text=Ol%C3%A1!%20Tenho%20interesse%20no%20Plano%20Simples%20Servi%C3%A7os%20(R$%20147).&type=phone_number&app_absent=0',
    style: 'solid',
  },
  {
    name: 'Plano Simples Comércio',
    price: 'R$ 197,00',
    desc: 'Para ME e EPP de comércio e indústria',
    highlighted: false,
    badge: null,
    features: [
      'Tudo do Plano Simples Serviços',
      'Apuração e escrituração de ICMS dentro do Simples',
      'SPED Fiscal (EFD-ICMS/IPI) quando obrigatório',
      'Orientação para emissão de NF-e e NFC-e',
      'Acompanhamento de substituição tributária (ICMS-ST)',
      'Orientação para controle de estoque e CMV',
      'Inventário anual',
      'Funcionário CLT extra: +R$ 19,90/mês',
    ],
    cta: 'Quero o plano Comércio',
    link: 'https://api.whatsapp.com/send/?phone=5562994767293&text=Ol%C3%A1!%20Tenho%20interesse%20no%20Plano%20Simples%20Com%C3%A9rcio%20(R$%20197).&type=phone_number&app_absent=0',
    style: 'outline',
  },
];

const avulsos = [
  { servico: 'IRPF (Imposto de Renda Pessoa Física)', valor: 'R$ 249,00' },
  { servico: 'Alteração Contratual (EI e LTDA)', valor: 'R$ 799,00 + taxas' },
  { servico: 'Alteração de Natureza Jurídica', valor: 'R$ 799,00 + taxas' },
  { servico: 'Baixa MEI', valor: 'R$ 139,90' },
  { servico: 'Baixa de empresa Simples Nacional', valor: 'R$ 799,00' },
  { servico: 'Migração de MEI para ME', valor: 'R$ 799,00' },
  { servico: 'Taxa de Reprocessamento (alterações/constituição)', valor: 'R$ 199,90' },
  { servico: 'Declaração de Faturamento', valor: 'R$ 59,00' },
  { servico: 'Reemissão de Guias', valor: 'R$ 59,90' },
  { servico: 'Formulários Diversos', valor: 'R$ 59,00' },
  { servico: 'Parcelamento de Impostos', valor: 'R$ 249,00' },
  { servico: 'Verificação de Pendências', valor: 'R$ 159,00' },
  { servico: 'Retificação de DAS', valor: 'R$ 59,90' },
  { servico: 'Retificação de Impostos', valor: 'R$ 59,90' },
];

export default function Plans() {
  const sectionRef = useScrollAnimation();
  const [avulsosOpen, setAvulsosOpen] = useState(false);

  return (
    <section
      id="planos"
      data-testid="plans-section"
      ref={sectionRef}
      className="py-24 md:py-32 bg-[#F8FAFC]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        {/* Header */}
        <div className="scroll-fade text-center mb-16 md:mb-20">
          <p className="text-sm uppercase tracking-[0.2em] font-semibold text-[#10B981] mb-4">
            Nossos Planos
          </p>
          <h2
            className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0A2540] tracking-tight leading-tight"
            style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '-1px' }}
          >
            Preço transparente, sem letra miúda
          </h2>
          <p className="text-[#64748B] text-base sm:text-lg mt-4 max-w-lg mx-auto">
            Escolha o plano que combina com o momento da sua empresa.
          </p>
        </div>

        {/* Plan Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-5 stagger-children mb-16">
          {plans.map((plan) => (
            <div
              key={plan.name}
              data-testid={`plan-card-${plan.name.toLowerCase().replace(/\s/g, '-')}`}
              className={`scroll-fade relative flex flex-col bg-white rounded-xl p-8 transition-all duration-300 hover:-translate-y-1 ${
                plan.highlighted
                  ? 'border-2 border-[#10B981] shadow-lg md:scale-105'
                  : 'border border-slate-200'
              }`}
            >
              {/* Badge */}
              {plan.badge && (
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                  <span
                    data-testid="plan-badge-popular"
                    className="bg-[#10B981] text-white text-xs font-bold px-4 py-1.5 rounded-2xl uppercase tracking-wider"
                  >
                    {plan.badge}
                  </span>
                </div>
              )}

              {/* Plan Header */}
              <div className="mb-6">
                <h3 className="text-lg font-bold text-[#0A2540] mb-1">{plan.name}</h3>
                <p className="text-[#64748B] text-sm">{plan.desc}</p>
              </div>

              {/* Price */}
              <div className="mb-6">
                <span
                  className="text-3xl sm:text-4xl font-bold text-[#0A2540]"
                  style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '-1px' }}
                >
                  {plan.price}
                </span>
                <span className="text-[#64748B] text-sm ml-1">/mês</span>
              </div>

              {/* Features */}
              <ul className="flex-1 space-y-3 mb-8">
                {plan.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-[#0F172A]">
                    <Check size={16} className="text-[#10B981] mt-0.5 flex-shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <a
                href={plan.link}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`plan-cta-${plan.name.toLowerCase().replace(/\s/g, '-')}`}
                className={`flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg font-semibold text-sm transition-colors ${
                  plan.style === 'solid'
                    ? 'bg-[#10B981] text-white hover:bg-[#059669]'
                    : 'border-2 border-[#0A2540] text-[#0A2540] hover:bg-[#0A2540] hover:text-white'
                }`}
              >
                {plan.cta}
                <ArrowRight size={16} />
              </a>
            </div>
          ))}
        </div>

        {/* Bonus Banner */}
        <div className="scroll-fade bg-[#ecfdf5] rounded-xl p-8 md:p-10 mb-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-[#10B981]/20 rounded-lg text-[#10B981]">
              <Gift size={22} />
            </div>
            <div>
              <h3 className="text-lg font-bold text-[#0A2540] mb-2">
                Abertura de empresa gratuita
              </h3>
              <p className="text-[#0F172A] text-sm sm:text-base leading-relaxed">
                Ao contratar qualquer plano na modalidade anual (12 meses), a abertura da sua empresa é por nossa conta.
                Você paga apenas as taxas oficiais (Junta Comercial, alvará, certificado digital) — os honorários ficam com a gente.
                CNPJ rápido, enquadramento correto, tudo online.
              </p>
            </div>
          </div>
        </div>

        {/* Avulsos Accordion */}
        <div className="scroll-fade border border-slate-200 rounded-xl bg-white overflow-hidden">
          <button
            data-testid="avulsos-toggle"
            onClick={() => setAvulsosOpen(!avulsosOpen)}
            className="w-full flex items-center justify-between px-8 py-5 text-left hover:bg-slate-50 transition-colors"
          >
            <span className="text-sm font-medium text-[#64748B]">
              Precisa de algo específico? Ver serviços avulsos (opcional)
            </span>
            {avulsosOpen ? (
              <Minus size={18} className="text-[#64748B]" />
            ) : (
              <Plus size={18} className="text-[#64748B]" />
            )}
          </button>

          {avulsosOpen && (
            <div className="px-8 pb-8" data-testid="avulsos-content">
              <p className="text-sm text-[#64748B] mb-5 italic">
                Estes são serviços que raramente são usados no dia a dia, mas quando você precisar, atendemos.
              </p>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 pr-4 font-semibold text-[#0A2540]">Serviço</th>
                      <th className="text-right py-3 pl-4 font-semibold text-[#0A2540]">Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {avulsos.map((item, i) => (
                      <tr key={i} className="border-b border-slate-100 last:border-0">
                        <td className="py-3 pr-4 text-[#0F172A]">{item.servico}</td>
                        <td className="py-3 pl-4 text-right text-[#0F172A] font-medium whitespace-nowrap">{item.valor}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <p className="text-xs text-[#64748B] mt-5">
                Taxas oficiais (Junta Comercial, alvará, certificados) sempre por conta do cliente. Clientes com plano ativo têm 10% de desconto nesses valores.
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
